import { useState, useMemo, useRef, useEffect } from 'react';
import styles from './PlaceholdersPanel.module.css';
import { FaChevronDown, FaChevronRight, FaPlus } from 'react-icons/fa';
import {
  countVariableUsage,
  extractCustomKeysFromContent,
  humanizarChave,
  isFixedKey,
  isValidCustomKey,
  normalizeCustomKey,
} from '../contract-editor/utils/variable-utils';

// Enum para padronizar os nomes dos placeholders
export enum PlaceholderEnum {
  NOME_LOCATARIO = 'nome_locatario',
  DOCUMENTO_LOCATARIO = 'documento_locatario',
  ENDERECO_LOCATARIO = 'endereco_locatario',
  RG_LOCATARIO = 'rg_locatario',
  VALOR_ALUGUEL = 'valor_aluguel',
  VALOR_CAUCAO = 'valor_caucao',
  DIA_PAGAMENTO = 'dia_pagamento',
  KM_INICIAL = 'km_inicial',
  PRAZO_MINIMO = 'prazo_minimo',
  PERIODICIDADE = 'periodicidade',
  DATA_RETIRADA = 'data_retirada',
  DATA_INICIO_PAGAMENTO = 'data_inicio_pagamento',
  PLACA = 'placa',
  MARCA = 'marca',
  MODELO = 'modelo',
  ANO = 'ano',
  SOMA_VALORES = 'soma_valores',
  RENAVAM = 'renavam',
  COR = 'cor',
  TELEFONE_LOCATARIO = 'telefone_locatario',
  EMAIL_LOCATARIO = 'email_locatario',
  CNH_LOCATARIO = 'cnh_locatario',
  CHASSI = 'chassi',
  FIADOR = 'fiador',
  VALOR_VEICULO = 'valor_veiculo',
  DATA_ENCERRAMENTO = 'data_encerramento',
  DATA_ATUAL = 'data_atual',
  FIPE_VEICULO = 'fipe_veiculo',
  OBSERVACAO = 'observacao',
  NEGOCIACAO = 'negociacao'
}

// Interface para mapear os placeholders com seus valores
export interface PlaceholderValues {
  [PlaceholderEnum.NOME_LOCATARIO]?: string;
  [PlaceholderEnum.DOCUMENTO_LOCATARIO]?: string;
  [PlaceholderEnum.ENDERECO_LOCATARIO]?: string;
  [PlaceholderEnum.RG_LOCATARIO]?: string;
  [PlaceholderEnum.VALOR_ALUGUEL]?: string;
  [PlaceholderEnum.VALOR_CAUCAO]?: string;
  [PlaceholderEnum.DIA_PAGAMENTO]?: string;
  [PlaceholderEnum.KM_INICIAL]?: string;
  [PlaceholderEnum.PRAZO_MINIMO]?: string;
  [PlaceholderEnum.PERIODICIDADE]?: string;
  [PlaceholderEnum.DATA_RETIRADA]?: string;
  [PlaceholderEnum.DATA_INICIO_PAGAMENTO]?: string;
  [PlaceholderEnum.PLACA]?: string;
  [PlaceholderEnum.MARCA]?: string;
  [PlaceholderEnum.MODELO]?: string;
  [PlaceholderEnum.ANO]?: string;
  [PlaceholderEnum.SOMA_VALORES]?: string;
  [PlaceholderEnum.RENAVAM]?: string;
  [PlaceholderEnum.COR]?: string;
  [PlaceholderEnum.TELEFONE_LOCATARIO]?: string;
  [PlaceholderEnum.EMAIL_LOCATARIO]?: string;
  [PlaceholderEnum.CNH_LOCATARIO]?: string;
  [PlaceholderEnum.CHASSI]?: string;
  [PlaceholderEnum.FIADOR]?: string;
  [PlaceholderEnum.VALOR_VEICULO]?: string;
  [PlaceholderEnum.DATA_ENCERRAMENTO]?: string;
  [PlaceholderEnum.DATA_ATUAL]?: string;
  [PlaceholderEnum.FIPE_VEICULO]?: string;
  [PlaceholderEnum.OBSERVACAO]?: string;
  [PlaceholderEnum.NEGOCIACAO]?: string;
}

// Função para substituir placeholders no documento
export const replacePlaceholders = (content: string, values: PlaceholderValues): string => {
  let result = content;

  Object.entries(values).forEach(([key, value]) => {
    const placeholder = `{${key}}`;
    result = result.replace(new RegExp(placeholder, 'g'), value || '');
  });

  return result;
};

export interface Placeholder {
  id: string;
  name: PlaceholderEnum;
  category: string;
  description?: string;
  usageCount?: number;
}

interface PlaceholdersPanelProps {
  onInsertPlaceholder: (placeholder: PlaceholderEnum) => void;
  onInsertCustomPlaceholder?: (key: string) => void;
  documentContent?: string;
}

const CATEGORIA_CUSTOM = 'Personalizadas';

const defaultPlaceholders: Placeholder[] = [
  { id: '1', name: PlaceholderEnum.NOME_LOCATARIO, category: 'Dados Locatário', description: 'Nome do Locatário' },
  { id: '2', name: PlaceholderEnum.DOCUMENTO_LOCATARIO, category: 'Dados Locatário', description: 'CPF/CNPJ do Locatário' },
  { id: '3', name: PlaceholderEnum.ENDERECO_LOCATARIO, category: 'Dados Locatário', description: 'Endereço do Locatário' },
  { id: '4', name: PlaceholderEnum.RG_LOCATARIO, category: 'Dados Locatário', description: 'RG do Locatário' },
  { id: '5', name: PlaceholderEnum.CNH_LOCATARIO, category: 'Dados Locatário', description: 'CNH do Locatário' },
  { id: '6', name: PlaceholderEnum.TELEFONE_LOCATARIO, category: 'Dados Locatário', description: 'Telefone do Locatário' },
  { id: '7', name: PlaceholderEnum.EMAIL_LOCATARIO, category: 'Dados Locatário', description: 'Email do Locatário' },

  { id: '8', name: PlaceholderEnum.VALOR_ALUGUEL, category: 'Contrato', description: 'Valor do Aluguel' },
  { id: '9', name: PlaceholderEnum.VALOR_CAUCAO, category: 'Contrato', description: 'Valor da Caução' },
  { id: '10', name: PlaceholderEnum.DIA_PAGAMENTO, category: 'Contrato', description: 'Dia do Pagamento' },
  { id: '11', name: PlaceholderEnum.KM_INICIAL, category: 'Contrato', description: 'Quilometragem inicial' },
  { id: '12', name: PlaceholderEnum.PRAZO_MINIMO, category: 'Contrato', description: 'Prazo mínimo do contrato' },
  { id: '13', name: PlaceholderEnum.PERIODICIDADE, category: 'Contrato', description: 'Periodicidade de Recebimento' },
  { id: '14', name: PlaceholderEnum.DATA_RETIRADA, category: 'Contrato', description: 'Data que o veículo foi entregue' },
  { id: '15', name: PlaceholderEnum.DATA_INICIO_PAGAMENTO, category: 'Contrato', description: 'Data quando iniciou os pagamentos' },
  { id: '16', name: PlaceholderEnum.FIADOR, category: 'Contrato', description: 'Fiador' },
  { id: '17', name: PlaceholderEnum.SOMA_VALORES, category: 'Contrato', description: 'Soma dos Valores (Aluguel + Caução)' },

  { id: '18', name: PlaceholderEnum.PLACA, category: 'Veículo', description: 'Placa do veículo' },
  { id: '19', name: PlaceholderEnum.MARCA, category: 'Veículo', description: 'Marca do veículo' },
  { id: '20', name: PlaceholderEnum.MODELO, category: 'Veículo', description: 'Modelo do veículo' },
  { id: '21', name: PlaceholderEnum.ANO, category: 'Veículo', description: 'Ano do veículo' },
  { id: '22', name: PlaceholderEnum.CHASSI, category: 'Veículo', description: 'Chassi do veículo' },
  { id: '23', name: PlaceholderEnum.RENAVAM, category: 'Veículo', description: 'Renavam' },
  { id: '24', name: PlaceholderEnum.COR, category: 'Veículo', description: 'Cor' },
  { id: '25', name: PlaceholderEnum.VALOR_VEICULO, category: 'Veículo', description: 'Valor do veículo' },
  { id: '26', name: PlaceholderEnum.FIPE_VEICULO, category: 'Veículo', description: 'Valor FIPE do veículo (consulta pela placa)' },

  { id: '27', name: PlaceholderEnum.DATA_ENCERRAMENTO, category: 'Contrato', description: 'Data de encerramento do contrato' },
  { id: '28', name: PlaceholderEnum.DATA_ATUAL, category: 'Contrato', description: 'Data atual (geração do contrato)' },

  // Variáveis manuais — preenchidas no ato da geração do contrato (não são auto-preenchidas)
  { id: '29', name: PlaceholderEnum.OBSERVACAO, category: 'Manuais (preenchidas ao gerar)', description: 'Observação livre — escrita no momento da geração' },
  { id: '30', name: PlaceholderEnum.NEGOCIACAO, category: 'Manuais (preenchidas ao gerar)', description: 'Detalhes da negociação — escritos no momento da geração' },
];


export function PlaceholdersPanel({ onInsertPlaceholder, onInsertCustomPlaceholder, documentContent }: PlaceholdersPanelProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['Dados Locatário', 'Contrato', 'Veículo', 'Manuais (preenchidas ao gerar)', CATEGORIA_CUSTOM])
  );

  const [creatingCustom, setCreatingCustom] = useState(false);
  const [customInput, setCustomInput] = useState('');
  const [customError, setCustomError] = useState<string | null>(null);
  const customInputRef = useRef<HTMLInputElement | null>(null);

  const customKeys = useMemo(() => extractCustomKeysFromContent(documentContent), [documentContent]);

  const placeholdersWithUsage = useMemo(() => {
    const counts = countVariableUsage(documentContent);
    return defaultPlaceholders.map(placeholder => ({
      ...placeholder,
      usageCount: counts.get(placeholder.name) ?? 0,
    }));
  }, [documentContent]);

  // Agrupar por categoria com useMemo
  const groupedPlaceholders = useMemo(() => {
    return placeholdersWithUsage.reduce((acc, placeholder) => {
      if (!acc[placeholder.category]) {
        acc[placeholder.category] = [];
      }
      acc[placeholder.category].push(placeholder);
      return acc;
    }, {} as Record<string, Placeholder[]>);
  }, [placeholdersWithUsage]);

  // Filtrar por termo de busca com useMemo
  const filteredGroups = useMemo(() => {
    return Object.entries(groupedPlaceholders).reduce((acc, [category, items]) => {
      const filteredItems = items.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.toLowerCase().includes(searchTerm.toLowerCase())
      );

      if (filteredItems.length > 0) {
        acc[category] = filteredItems;
      }
      return acc;
    }, {} as Record<string, Placeholder[]>);
  }, [groupedPlaceholders, searchTerm]);

  // Customs filtradas pela busca
  const filteredCustomKeys = useMemo(() => {
    if (!searchTerm) return customKeys;
    const lower = searchTerm.toLowerCase();
    return customKeys.filter(k => k.includes(lower) || humanizarChave(k).toLowerCase().includes(lower));
  }, [customKeys, searchTerm]);

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const handleInsert = (placeholderName: PlaceholderEnum) => {
    onInsertPlaceholder(placeholderName);
  };

  const validarChaveCustom = (raw: string): { ok: true; key: string } | { ok: false; error: string } => {
    const normalized = normalizeCustomKey(raw);
    if (!normalized) return { ok: false, error: 'Informe um nome para a variável.' };
    if (!isValidCustomKey(normalized)) {
      return { ok: false, error: 'Use apenas letras minúsculas, números e underscore (deve começar com letra).' };
    }
    if (isFixedKey(normalized)) {
      return { ok: false, error: 'Essa variável já existe no sistema. Use o painel ao lado para inseri-la.' };
    }
    return { ok: true, key: normalized };
  };

  const abrirCriacao = () => {
    setCreatingCustom(true);
    setCustomInput('');
    setCustomError(null);
  };

  const fecharCriacao = () => {
    setCreatingCustom(false);
    setCustomInput('');
    setCustomError(null);
  };

  const confirmarCriacao = () => {
    if (!onInsertCustomPlaceholder) {
      fecharCriacao();
      return;
    }
    const validacao = validarChaveCustom(customInput);
    if (!validacao.ok) {
      setCustomError(validacao.error);
      return;
    }
    onInsertCustomPlaceholder(validacao.key);
    fecharCriacao();
  };

  useEffect(() => {
    if (creatingCustom) {
      // Foco automático para fluxo ágil de criação.
      setTimeout(() => customInputRef.current?.focus(), 0);
    }
  }, [creatingCustom]);

  const handleInsertCustomKey = (key: string) => {
    onInsertCustomPlaceholder?.(key);
  };

  const customCategoryMatchesSearch = !searchTerm || CATEGORIA_CUSTOM.toLowerCase().includes(searchTerm.toLowerCase()) || filteredCustomKeys.length > 0;
  const semResultados = Object.keys(filteredGroups).length === 0 && !customCategoryMatchesSearch;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.search}>
          <input
            type="text"
            placeholder="Buscar variáveis..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        {onInsertCustomPlaceholder && (
          <button
            type="button"
            className={styles.newVariableButton}
            onClick={abrirCriacao}
            disabled={creatingCustom}
            title="Criar uma variável customizada que será preenchida no momento da geração do contrato"
          >
            <FaPlus /> Nova variável
          </button>
        )}
        {creatingCustom && (
          <div className={styles.customCreator}>
            <input
              ref={customInputRef}
              type="text"
              value={customInput}
              onChange={(e) => {
                setCustomInput(e.target.value);
                setCustomError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  confirmarCriacao();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  fecharCriacao();
                }
              }}
              placeholder="ex: clausula_garantia"
              className={styles.customInput}
            />
            {customError && <div className={styles.customError}>{customError}</div>}
            <div className={styles.customActions}>
              <button type="button" className={styles.customCancelButton} onClick={fecharCriacao}>
                Cancelar
              </button>
              <button type="button" className={styles.customConfirmButton} onClick={confirmarCriacao}>
                Inserir
              </button>
            </div>
            <div className={styles.customHint}>
              Apenas letras minúsculas, números e underscore. Será preenchida no momento de gerar o contrato.
            </div>
          </div>
        )}
      </div>

      <div className={styles.content}>
        {Object.entries(filteredGroups).map(([category, items]) => (
          <div key={category} className={styles.category}>
            <div
              className={styles.categoryHeader}
              onClick={() => toggleCategory(category)}
            >
              <span className={styles.categoryName}>{category}</span>
              <span className={styles.arrow}>
                {expandedCategories.has(category) ? <FaChevronDown /> : <FaChevronRight />}
              </span>
            </div>

            {expandedCategories.has(category) && (
              <div className={styles.items}>
                {items.map((item) => (
                  <div
                    key={item.id}
                    className={styles.item}
                    onClick={() => handleInsert(item.name)}
                    title={item.description}
                  >
                    <div className={styles.itemMain}>
                      <span className={styles.itemName}>{item.name}</span>
                      {!!item.usageCount && item.usageCount > 0 && (
                        <span className={styles.usageCount}>{item.usageCount}</span>
                      )}
                    </div>
                    {item.description && (
                      <div className={styles.itemDescription}>{item.description}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {onInsertCustomPlaceholder && customCategoryMatchesSearch && (
          <div className={styles.category}>
            <div
              className={styles.categoryHeader}
              onClick={() => toggleCategory(CATEGORIA_CUSTOM)}
            >
              <span className={styles.categoryName}>{CATEGORIA_CUSTOM}</span>
              <span className={styles.arrow}>
                {expandedCategories.has(CATEGORIA_CUSTOM) ? <FaChevronDown /> : <FaChevronRight />}
              </span>
            </div>
            {expandedCategories.has(CATEGORIA_CUSTOM) && (
              <div className={styles.items}>
                {filteredCustomKeys.length === 0 ? (
                  <div className={styles.customEmpty}>
                    Nenhuma variável personalizada ainda. Clique em "Nova variável" acima para criar uma.
                  </div>
                ) : (
                  filteredCustomKeys.map((key) => (
                    <div
                      key={key}
                      className={`${styles.item} ${styles.itemCustom}`}
                      onClick={() => handleInsertCustomKey(key)}
                      title="Variável personalizada — clique para inserir outra ocorrência"
                    >
                      <div className={styles.itemMain}>
                        <span className={styles.itemName}>{key}</span>
                      </div>
                      <div className={styles.itemDescription}>{humanizarChave(key)}</div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {semResultados && (
          <div className={styles.empty}>
            Nenhuma variável encontrada
          </div>
        )}
      </div>
    </div>
  );
}
